/**
 * One place being placed or changed: the address, the entrance, what to know there, the other
 * names it goes by, and the two points the map beside it sets — the door, and where the car
 * stops when that is somewhere else (#420, #433).
 *
 * Placing starts from the building: found through the address search, which opens the map on
 * it, and then each door is a click. After one is saved, *Next entrance* keeps the building
 * and offers the next letter — А, Б, В — so a block's five doors are five clicks and five
 * saves rather than five forms.
 *
 * An address Google named, arriving from the report (#478), may not be saved as Google's: the
 * editor says what still has to change — the words, the point — and *Save* waits for both.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { geoApi, type GeocodeResult } from '../../orders/api';
import type { PlaceDraft } from '../../places/api';
import { stillGoogles, type GoogleNamed } from '../../places/google';
import type { MapPoint } from '../../places/PlacesMap';
import { Banner, Button, Card, CardHeader, Field, Input, Segmented, Textarea } from '../../ui';

export function PlaceEditor({
  draft,
  google = null,
  isNew,
  picking,
  busy,
  problem,
  onChange,
  onPicking,
  onJump,
  onSave,
  onCancel,
  onDelete,
  onNext,
}: {
  draft: PlaceDraft;
  /** Google's name and point, when the address came from one of Google's; null otherwise. */
  google?: GoogleNamed | null;
  isNew: boolean;
  /** What a click on the map sets now. */
  picking: 'door' | 'stop';
  busy: boolean;
  problem: string | null;
  onChange: (next: PlaceDraft) => void;
  onPicking: (next: 'door' | 'stop') => void;
  /** Sends the map to a building just found. */
  onJump: (point: MapPoint) => void;
  onSave: () => void;
  onCancel: () => void;
  /** The administrator's alone; absent while placing. */
  onDelete?: () => void;
  /** Keeps the building and offers the next letter, after a door was saved. */
  onNext?: () => void;
}) {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [found, setFound] = useState<GeocodeResult[] | null>(null);
  const [names, setNames] = useState(draft.names?.join(', ') ?? '');

  const hasDoor = Number.isFinite(draft.longitude) && draft.longitude !== 0;
  const unmet = google === null ? [] : stillGoogles(draft, google);
  const hasStop = draft.stopLongitude != null && draft.stopLatitude != null;

  async function find() {
    if (query.trim().length === 0) return;

    // As the desk's automatic position searches: the firm's town first, and further only when
    // the town has nothing — a whole-country search lists every village's Максим Горки 5 above
    // the one in town.
    setFound(await geoApi.searchWidening(query.trim()).then((answer) => answer.results, () => []));
  }

  return (
    <Card tone="brand">
      <CardHeader title={isNew ? t('places.editor.addTitle') : t('places.editor.editTitle')} />

      <div className="flex flex-col gap-3 px-3.5 pb-3.5">
        {isNew && (
          <Field label={t('places.editor.find')} help={t('places.editor.findHelp')}>
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;

                  event.preventDefault();
                  void find();
                }}
                autoComplete="off"
              />
              <Button onClick={() => void find()}>{t('places.editor.findButton')}</Button>
            </div>
          </Field>
        )}

        {found !== null && (
          <ul className="m-0 p-0 list-none flex flex-col gap-1 max-h-48 overflow-y-auto">
            {found.length === 0 && <li className="text-secondary text-ink-3">{t('places.editor.nothingFound')}</li>}
            {found.map((result, index) => (
              <li key={`${result.address}-${index}`}>
                <button
                  type="button"
                  className="w-full text-left px-2 py-1 rounded-control hover:bg-surface-2"
                  onClick={() => {
                    onChange({ ...draft, building: result.firmPlace?.building ?? result.address });
                    onJump(result.location);
                    setFound(null);
                  }}
                >
                  {result.address}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-[1fr_96px] gap-3">
          <Field id="building" label={t('places.building')} help={t('places.buildingHelp')}>
            <Input
              value={draft.building}
              onChange={(event) => onChange({ ...draft, building: event.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field id="entrance" label={t('places.entrance')}>
            <Input
              value={draft.entrance ?? ''}
              maxLength={2}
              onChange={(event) =>
                onChange({ ...draft, entrance: event.target.value.toLocaleUpperCase('bg') || null })
              }
              autoComplete="off"
            />
          </Field>
        </div>

        <Field id="note" label={t('places.note')} help={t('places.noteHelp')}>
          <Textarea
            rows={2}
            maxLength={300}
            value={draft.note ?? ''}
            onChange={(event) => onChange({ ...draft, note: event.target.value || null })}
          />
        </Field>

        <Field id="names" label={t('places.names')} help={t('places.namesHelp')}>
          <Input
            value={names}
            onChange={(event) => {
              setNames(event.target.value);
              onChange({
                ...draft,
                names: event.target.value
                  .split(',')
                  .map((name) => name.trim())
                  .filter((name) => name.length > 0),
              });
            }}
            autoComplete="off"
          />
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-secondary text-ink-2">{t('places.editor.clickSets')}</span>
          <Segmented
            label={t('places.editor.clickSets')}
            value={picking}
            compact
            options={[
              { value: 'door', label: t('places.map.door') },
              { value: 'stop', label: t('places.map.stop') },
            ]}
            onChange={onPicking}
          />
        </div>

        <p className="m-0 text-secondary text-ink-3">
          {hasDoor ? t('places.editor.doorSet') : t('places.editor.doorMissing')}
          {' '}
          {hasStop ? t('places.editor.stopSet') : t('places.editor.stopAtDoor')}
          {hasStop && (
            <>
              {' '}
              <button
                type="button"
                className="text-brand hover:underline"
                onClick={() => onChange({ ...draft, stopLongitude: null, stopLatitude: null })}
              >
                {t('places.editor.stopClear')}
              </button>
            </>
          )}
        </p>

        {unmet.length > 0 && (
          <Banner tone="warn">
            <span className="block">{t('places.google.why')}</span>
            {unmet.map((what) => (
              <span key={what} className="block">
                · {t(`places.google.${what}`)}
              </span>
            ))}
          </Banner>
        )}

        {problem !== null && <Banner tone="danger">{problem}</Banner>}

        <div className="flex flex-wrap justify-between gap-2">
          <span className="flex gap-2">
            {onDelete !== undefined && (
              <Button variant="danger" disabled={busy} onClick={onDelete}>
                {t('places.editor.delete')}
              </Button>
            )}
            {onNext !== undefined && (
              <Button disabled={busy} onClick={onNext}>
                {t('places.editor.next')}
              </Button>
            )}
          </span>
          <span className="flex gap-2">
            <Button disabled={busy} onClick={onCancel}>
              {t('settings.discard')}
            </Button>
            <Button
              variant="primary"
              disabled={busy || !hasDoor || draft.building.trim().length === 0 || unmet.length > 0}
              onClick={onSave}
            >
              {busy ? t('settings.saving') : t('settings.save')}
            </Button>
          </span>
        </div>
      </div>
    </Card>
  );
}
